/**
 * Handing a file to the user.
 *
 * The native build staged exports on disk because an iOS share sheet needs a
 * real file. The web build generates the bytes and offers them directly, which
 * *removes* the plaintext-accumulation risk that staging existed to manage
 * (spec §9.3) — journal Markdown never sits in a scratch directory waiting for a
 * sweep. That is why this port takes bytes and not a path: a path would bring
 * the staging lifecycle back with it.
 */

export type ExportOutcome =
  /** The user chose a destination and the bytes were written. */
  | "saved"
  /** The user dismissed the picker. Not an error, and never surfaced as one. */
  | "cancelled"
  /** The platform refused or the write failed. The caller keeps the content. */
  | "failed";

export const EXPORT_UNSAFE_FILENAME_ERROR = "Export filename is not a plain file name";

/**
 * Rejects anything that is not a single plain file name.
 *
 * Every export name is built from user content — an entry title, a session date
 * — and a title containing a slash would otherwise become a path the save picker
 * interprets. This is the same rule the staging layer enforces for removals, for
 * the same reason: path construction is where a naming bug turns into writing
 * somewhere nobody asked for. Throwing, rather than sanitising, keeps the fault
 * attributable to whoever built the name.
 */
export function assertSafeExportFilename(filename: string): void {
  const isUnsafe =
    filename.length === 0 ||
    filename !== filename.trim() ||
    filename === "." ||
    filename === ".." ||
    filename.includes("/") ||
    filename.includes("\\") ||
    // Control characters are legal in some filesystems and invisible in all of
    // them, which makes them a way to hide what a file is actually called.
    /[\u0000-\u001f\u007f]/u.test(filename);

  if (isUnsafe) {
    throw new RangeError(EXPORT_UNSAFE_FILENAME_ERROR);
  }
}

export interface ExportPort {
  /**
   * Offers `bytes` to the user as `filename`. Resolves with the outcome and
   * never rejects except for an unsafe filename, which is a programming error
   * rather than a runtime condition — see {@link assertSafeExportFilename}. That
   * one case rejects rather than throwing synchronously, so a caller has exactly
   * one failure channel to handle instead of a `try` wrapped around an `await`.
   *
   * Implementations prefer `showSaveFilePicker()` and fall back to a Blob
   * download. The fallback cannot report whether the user kept the file, so it
   * resolves `"saved"` once the download has been handed to the browser: the
   * caller must not treat `"saved"` as proof the file exists, only as proof the
   * app did its part.
   */
  offer(filename: string, mimeType: string, bytes: Uint8Array): Promise<ExportOutcome>;
}
